import React from "react";
import { Navigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { hasRole, hasAnyRole, hasAppRole } from "../utils/authUtils";
import { UserRole } from "../config/authConfig";
import { getPreferredMsalAccount } from "../utils/msalAccountUtils";
import { Loading } from "./Loading";
import { useApiAuth } from "../hooks/useApiAuth";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: UserRole;
  requiredRoles?: UserRole[]; // Allow multiple acceptable Entra JWT roles
  requiredAppRoles?: UserRole[]; // App RBAC roles from GET /me/roles
  requireAnyRole?: boolean;
  redirectTo?: string;
}

/**
 * Component that protects routes based on user authentication and roles
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  requiredRoles,
  requiredAppRoles,
  requireAnyRole = false,
  redirectTo = "/unauthorized",
}) => {
  const { accounts, instance } = useMsal();
  const account = getPreferredMsalAccount(instance, accounts);
  const { hasAttemptedAuth, appRoles, appRolesLoaded } = useApiAuth();

  if (!hasAttemptedAuth) {
    return <Loading message="Checking authentication status..." fullScreen />;
  }

  // If no user is authenticated, this should not happen as the app requires auth
  if (!account) {
    return <Navigate to={redirectTo} replace />;
  }

  // If requireAnyRole is true, check if user has any valid role
  if (requireAnyRole && !hasAnyRole(account)) {
    return <Navigate to="/unauthorized" replace />;
  }

  const needsAppRoles =
    requiredAppRoles !== undefined && requiredAppRoles.length > 0;
  if (needsAppRoles && !appRolesLoaded) {
    return <Loading message="Loading permissions..." fullScreen />;
  }

  // App RBAC roles (e.g. red_team / infra_admin)
  if (needsAppRoles) {
    const hasAnyRequiredAppRole = requiredAppRoles!.some((role) =>
      hasAppRole(appRoles, role),
    );
    if (!hasAnyRequiredAppRole) {
      return (
        <div className="p-4">
          <div className="p-message p-message-error">
            <div className="p-message-wrapper">
              <span className="p-message-icon pi pi-times-circle"></span>
              <div className="p-message-text">
                <span className="p-message-summary">Access Denied</span>
                <div className="p-message-detail">
                  You don't have the required permissions to access this page.
                  Required roles: {requiredAppRoles!.join(", ")}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return <>{children}</>;
  }

  // Check for multiple acceptable Entra JWT roles first
  if (requiredRoles && requiredRoles.length > 0) {
    const hasAnyRequiredRole = requiredRoles.some((role) =>
      hasRole(account, role),
    );
    if (!hasAnyRequiredRole) {
      return (
        <div className="p-4">
          <div className="p-message p-message-error">
            <div className="p-message-wrapper">
              <span className="p-message-icon pi pi-times-circle"></span>
              <div className="p-message-text">
                <span className="p-message-summary">Access Denied</span>
                <div className="p-message-detail">
                  You don't have the required permissions to access this page.
                  Required roles: {requiredRoles.join(", ")}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
  }
  // If a specific role is required, check if user has it
  else if (requiredRole && !hasRole(account, requiredRole)) {
    return (
      <div className="p-4">
        <div className="p-message p-message-error">
          <div className="p-message-wrapper">
            <span className="p-message-icon pi pi-times-circle"></span>
            <div className="p-message-text">
              <span className="p-message-summary">Access Denied</span>
              <div className="p-message-detail">
                You don't have the required permissions to access this page.
                Required role: {requiredRole}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
